import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import { User } from "../../utils";
import moment from "moment";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { hostname, parseError, addImageDimensions } from "../../utils";

const ChatLog = forwardRef(
  (
    {
      companyId,
      getToken,
      currentUser,
      currentChatId,
      style,
      perPage = 25,
      eventListener,
      onFirstChatLoad,
    }: {
      companyId: string;
      getToken: Function;
      currentUser: any | User;
      currentChatId: any | string;
      style: any;
      perPage: number;
      eventListener: Function;
      onFirstChatLoad?: (firstChatId: string) => void;
    },
    ref
  ) => {
    const [chats, setChats] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const navigate = useNavigate();
    const observer = useRef();
    const [hasMore, setHasMore] = useState(true);
    const hasAutoSelected = useRef(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [chatToDelete, setChatToDelete] = useState<any>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [chatToEdit, setChatToEdit] = useState<any>(null);
    const [renameValue, setRenameValue] = useState<string>("");
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [menuChatId, setMenuChatId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);
    const lastChatElementRef = useCallback(
      (node: any) => {
        if (loading) return;
        // @ts-ignore
        if (observer.current) observer.current.disconnect();
        // @ts-ignore
        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPage((prevPage) => prevPage + 1);
          }
        });
        // @ts-ignore
        if (node) observer.current.observe(node);
      },
      [loading]
    );

    const getChats = async (append: boolean = false, search: string = "") => {
      setLoading(true);
      const currentToken = await getToken();
      const params: any = { page, perPage };
      if (search) {
        params.search = search;
      }
      await axios
        .get(`${hostname}/companies/${companyId}/chats`, {
          params,
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        .then((res) => {
          const newChats = res.data.chats;
          if (!append) {
            setChats(newChats);
            // Auto-select first chat if no chat is currently selected
            if (onFirstChatLoad && !currentChatId && newChats.length > 0 && !hasAutoSelected.current) {
              hasAutoSelected.current = true;
              onFirstChatLoad(newChats[0].id);
            }
          } else {
            setChats((prev: any[]) => [...prev, ...newChats]);
          }
          setHasMore(newChats.length === perPage);
        })
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        })
        .finally(() => {
          setLoading(false);
        });
    };

    useEffect(() => {
      if (page > 1) {
        getChats(true, searchQuery);
      }
    }, [page]);

    useEffect(() => {
      if (currentUser.uid) {
        setChats([]);
        setPage(1);
        hasAutoSelected.current = false; // Reset auto-selection flag
        getChats(false, searchQuery);
      }
    }, [currentUser.uid, companyId]);

    // Debounced search effect
    useEffect(() => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }

      searchDebounceTimer.current = setTimeout(() => {
        setChats([]);
        setPage(1);
        hasAutoSelected.current = false;
        getChats(false, searchQuery);
      }, 500);

      return () => {
        if (searchDebounceTimer.current) {
          clearTimeout(searchDebounceTimer.current);
        }
      };
    }, [searchQuery]);

    useImperativeHandle(ref, () => ({
      refreshChats: async () => {
        setLoading(true);
        const currentToken = await getToken();
        const params: any = { page: 1, perPage };
        if (searchQuery) {
          params.search = searchQuery;
        }
        await axios
          .get(`${hostname}/companies/${companyId}/chats`, {
            params,
            headers: { Authorization: `Bearer ${currentToken}` },
          })
          .then((res) => {
            const newChats = res.data.chats;
            setChats(newChats);
            setHasMore(newChats.length === perPage);
            setPage(1);
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            setLoading(false);
          });
      },
    }));

    const handleMenuClick = (chat: any, e: any) => {
      e.stopPropagation();
      e.preventDefault();
      setMenuAnchorEl(e.currentTarget);
      setMenuChatId(chat.id);
    };

    const handleMenuClose = () => {
      setMenuAnchorEl(null);
      setMenuChatId(null);
    };

    const handleMenuEdit = () => {
      const chat = chats.find((c: any) => c.id === menuChatId);
      if (chat) {
        setChatToEdit(chat);
        setRenameValue(chat.firstMsg || "");
        setEditDialogOpen(true);
      }
      handleMenuClose();
    };

    const handleMenuDelete = () => {
      const chat = chats.find((c: any) => c.id === menuChatId);
      if (chat) {
        setChatToDelete(chat);
        setConfirmDialogOpen(true);
      }
      handleMenuClose();
    };

    const handleConfirmDelete = async () => {
      if (!chatToDelete) return;

      const currentToken = await getToken();
      await axios
        .delete(`${hostname}/chats/${chatToDelete.id}`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        });
      setChats((prev) => prev.filter((c: any) => c.id !== chatToDelete.id));

      // If we just deleted the current chat, navigate away
      if (chatToDelete.id === currentChatId) {
        navigate(`/dashboard/companies/${companyId}/chats`);
      }

      setConfirmDialogOpen(false);
      setChatToDelete(null);
    };

    const handleCancelDelete = () => {
      setConfirmDialogOpen(false);
      setChatToDelete(null);
    };

    const handleEditDialogClose = () => {
      setEditDialogOpen(false);
      setChatToEdit(null);
      setRenameValue("");
    };

    const handleEditDialogSave = async () => {
      if (!chatToEdit || !renameValue.trim()) {
        return;
      }

      const currentToken = await getToken();
      await axios
        .patch(
          `${hostname}/chats/${chatToEdit.id}`,
          { firstMsg: renameValue },
          {
            headers: { Authorization: `Bearer ${currentToken}` },
          }
        )
        .then(() => {
          setChats((prev) =>
            prev.map((c: any) =>
              c.id === chatToEdit.id ? { ...c, firstMsg: renameValue } : c
            )
          );
          handleEditDialogClose();
        })
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        });
    };

    return (
      <div style={style}>
        <Box sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, title, or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery("")}
                    edge="end"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List sx={{ pt: 0 }}>
          {!loading && chats && chats.length === 0 && (
            <ListItem>
              <ListItemText sx={{ textAlign: "center" }}>
                {searchQuery ? "No conversations match your search" : "No Conversations Found"}
              </ListItemText>
            </ListItem>
          )}
          {chats.map((chat, idx) => (
            <React.Fragment key={chat.id}>
              <ListItemButton
                ref={idx === chats.length - 1 ? lastChatElementRef : null}
                disabled={chat.id === currentChatId}
                onClick={() =>
                  navigate(`/dashboard/companies/${companyId}/chats/${chat.id}`)
                }
                sx={{
                  '& .MuiListItemSecondaryAction-root': {
                    visibility: 'hidden',
                  },
                  '&:hover .MuiListItemSecondaryAction-root': {
                    visibility: 'visible',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar alt={chat.title} src={addImageDimensions(chat.picture, 80, 80)} />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      component="span"
                      variant="body1"
                      style={{
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        lineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {chat.firstMsg || "No messages"}
                    </Typography>
                  }
                  secondary={
                    <React.Fragment>
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        {chat.name} ({chat.title})
                      </Typography>
                      {" · "}
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                      >
                        {moment.unix(chat.ts / 1000).fromNow()}
                      </Typography>
                    </React.Fragment>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="more"
                    onClick={(e) => handleMenuClick(chat, e)}
                    size="small"
                    sx={{
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      padding: 0,
                      '& .MuiSvgIcon-root': {
                        fontSize: 16
                      }
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItemButton>
              {idx < chats.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          ))}
          {loading && hasMore && (
            <ListItem>
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            </ListItem>
          )}
        </List>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>Edit</MenuItem>
          <MenuItem onClick={handleMenuDelete}>Delete</MenuItem>
        </Menu>

        <Dialog
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Chat Caption</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Chat Caption"
              type="text"
              fullWidth
              multiline
              maxRows={3}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleEditDialogSave();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditDialogClose}>Cancel</Button>
            <Button onClick={handleEditDialogSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Delete Conversation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this conversation with{" "}
              {chatToDelete?.name}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
);

export default ChatLog;
